/**
 * CeremonieAM.jsx — Cérémonie de fin de formation permis AM.
 * Route : /permis-am/ceremonie
 * Format : présentation immersive fullscreen (façon Kahoot/keynote) pour projection
 *          devant élève + parents en fin de formation (~45 min).
 * Phases :
 *   1. Intro (bienvenue parents + fil conducteur)
 *   2. 7 slides thèmes S5.A→G (référentiel officiel)
 *   3. QCM 10 questions FUN
 *   4. Cérémonie finale : carte permis AM animée (fun, non officielle)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ChevronRight, ChevronLeft, Trophy, Sparkles, Check, X as XIcon,
  Users, BookOpen, ShieldCheck, RefreshCw, Home, Maximize2, Loader2, QrCode,
  AlertTriangle, Leaf, Wrench, Beer, Gauge, Baby, MessagesSquare, Play, Film,
  Download, FileDown, PenLine,
} from 'lucide-react';
import useSeo from '../../hooks/useSeo';
import { cours as sensibiCours } from '../../data/cours/permisAmSensibilisation';
import { quiz as quizCeremonie } from '../../data/quiz/quizCeremonieAm';
import { enterFullscreen } from '../../components/FullscreenMode';
import { CartePermisAM } from '../../components/CartePermisAM';
import { toast } from 'sonner';

/* Enrichissement des 15 sections du module Sensibilisation
   (mapping num → accent, videoId, source ONISR)
   Permet de garder la data source unique et d'y ajouter la couche cérémonie */
const ENRICHMENT_MAP = {
  '01': { accent: 'red',     source: 'ONISR · Étude construction des accidents' },
  '02': { accent: 'red',     videoId: 'JxZYE9BbdyY', videoLabel: 'Prévention · vulnérabilité en cyclomoteur', source: 'ONISR · Baromètre juillet 2026 · 5 304 blessés graves 2RM sur 12 mois' },
  '03': { accent: 'amber',   videoId: 'BzBttCRjr24', videoLabel: 'Prévention · vitesse et conséquences', source: 'Sécurité Routière · distances de freinage' },
  '04': { accent: 'purple',  source: 'ONISR · Distracteurs · 12,5 m à 45 km/h en 1 s' },
  '05': { accent: 'purple',  source: 'ONISR · Étude psychoactifs jeunes' },
  '06': { accent: 'orange',  source: 'ONISR · Fatigue et somnolence au volant' },
  '07': { accent: 'pink',    source: 'ONISR · Baromètre juillet 2026 · +29% jeunes <18 ans tués (201/an)' },
  '08': { accent: 'red',     source: 'Code de la route · article R431-1 · équipement obligatoire' },
  '09': { accent: 'sky',     source: 'ONISR · Étude passager 2RM' },
  '10': { accent: 'emerald', source: 'Sécurité Routière · check-list départ' },
  '11': { accent: 'orange',  videoId: '5nZu4Cp8d6c', videoLabel: 'Prévention · les conséquences du débridage', source: 'Code de la route · article R413-14 · débridage 135€' },
  '12': { accent: 'sky',     source: 'ONISR · Reconnaître la situation qui se dégrade' },
  '13': { accent: 'pink',    source: 'Sécurité Routière · dialogue famille' },
  '14': { accent: 'emerald', source: 'ONISR · Renoncer, c\'est décider' },
  '15': { accent: 'red',     source: 'Cérémonie de fin de formation · Code 2 Roues' },
};

/* Section transformée avec enrichissement pour l'affichage cinéma */
const enrichSection = (section) => ({
  ...section,
  ...(ENRICHMENT_MAP[section.num] || { accent: 'red' }),
});

/* Contenu intro dérivé du module Sensibilisation existant */
const intro = {
  eyebrow: 'Cérémonie de fin de formation',
  title: sensibiCours.title.replace('Module 3 — ', ''),
  subtitle: sensibiCours.subtitle,
  duree: '45-60 min · avec tes parents',
  fil: sensibiCours.intro,
  bulletPoints: [
    { icon: 'Users', text: 'Un moment partagé entre toi, tes parents et ton moniteur' },
    { icon: 'BookOpen', text: `${sensibiCours.sections.length} séquences officielles · Module 3 Sensibilisation` },
    { icon: 'Trophy', text: 'Un QCM Kahoot final en famille + carte permis AM' },
    { icon: 'ShieldCheck', text: 'Ton attestation officielle sera délivrée par ton auto-école' },
  ],
};

const themes = sensibiCours.sections.map(enrichSection);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* -------------------------------------------------------------
   Palette locale — 1 couleur par thème A→G
   ------------------------------------------------------------- */
const ACCENT_STYLES = {
  red:     { grad: 'from-red-600 via-red-700 to-red-950',           text: 'text-red-300',     ring: 'ring-red-500',     solid: 'bg-red-600',     border: 'border-red-500/60',     glow: 'shadow-[0_0_60px_-15px_rgba(220,38,38,0.7)]',   icon: AlertTriangle },
  emerald: { grad: 'from-emerald-600 via-emerald-700 to-emerald-950', text: 'text-emerald-300', ring: 'ring-emerald-500', solid: 'bg-emerald-600', border: 'border-emerald-500/60', glow: 'shadow-[0_0_60px_-15px_rgba(16,185,129,0.7)]', icon: Leaf },
  orange:  { grad: 'from-orange-500 via-orange-600 to-orange-950',   text: 'text-orange-300',  ring: 'ring-orange-500',  solid: 'bg-orange-500',  border: 'border-orange-500/60',  glow: 'shadow-[0_0_60px_-15px_rgba(249,115,22,0.7)]', icon: Wrench },
  purple:  { grad: 'from-purple-600 via-purple-700 to-purple-950',   text: 'text-purple-300',  ring: 'ring-purple-500',  solid: 'bg-purple-600',  border: 'border-purple-500/60',  glow: 'shadow-[0_0_60px_-15px_rgba(147,51,234,0.7)]', icon: Beer },
  amber:   { grad: 'from-amber-500 via-amber-600 to-amber-950',      text: 'text-amber-300',   ring: 'ring-amber-500',   solid: 'bg-amber-500',   border: 'border-amber-500/60',   glow: 'shadow-[0_0_60px_-15px_rgba(245,158,11,0.7)]', icon: Gauge },
  sky:     { grad: 'from-sky-600 via-sky-700 to-sky-950',            text: 'text-sky-300',     ring: 'ring-sky-500',     solid: 'bg-sky-600',     border: 'border-sky-500/60',     glow: 'shadow-[0_0_60px_-15px_rgba(14,165,233,0.7)]', icon: Baby },
  pink:    { grad: 'from-pink-600 via-pink-700 to-pink-950',         text: 'text-pink-300',    ring: 'ring-pink-500',    solid: 'bg-pink-600',    border: 'border-pink-500/60',    glow: 'shadow-[0_0_60px_-15px_rgba(236,72,153,0.7)]', icon: MessagesSquare },
};

/* -------------------------------------------------------------
   Slide 0 — INTRO (bienvenue parents + fil conducteur)
   ------------------------------------------------------------- */
function IntroSlide({ onStart }) {
  const iconMap = { Users, BookOpen, Trophy, ShieldCheck };
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-700 via-red-900 to-black text-white p-6 sm:p-10 relative overflow-hidden"
      data-testid="ceremonie-intro"
    >
      {/* Décor : grain + halo */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,200,100,0.1), transparent 40%)" }} />

      <div className="relative z-10 max-w-4xl w-full text-center space-y-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[10px] sm:text-xs font-black uppercase tracking-[0.24em]">
          <Sparkles className="w-3 h-3" /> {intro.eyebrow} · {intro.duree}
        </div>

        <h1
          className="text-4xl sm:text-6xl md:text-7xl font-black leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {intro.title}
          <br />
          <span className="text-red-200 italic font-normal text-2xl sm:text-4xl md:text-5xl">
            {intro.subtitle}
          </span>
        </h1>

        <div className="grid sm:grid-cols-2 gap-3 text-left max-w-3xl mx-auto">
          {intro.bulletPoints.map((b, i) => {
            const Icon = iconMap[b.icon] || Sparkles;
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-white/8 backdrop-blur border border-white/15"
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-red-200" />
                </div>
                <p className="text-sm sm:text-base leading-snug pt-1">{b.text}</p>
              </div>
            );
          })}
        </div>

        <p className="text-sm sm:text-base italic text-red-100/90 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
          {intro.fil}
        </p>

        <Button
          onClick={onStart}
          size="lg"
          className="bg-white text-red-700 hover:bg-red-100 font-black uppercase tracking-widest px-8 py-6 text-base sm:text-lg shadow-2xl"
          data-testid="ceremonie-start-btn"
        >
          Commencer la cérémonie <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   Slide thème (A→G) — version CINÉMA BROADCAST TV
   - Ken Burns sur les halos (dérive + zoom lent en boucle)
   - Particules flottantes qui montent
   - Compteur animé sur la stat (count-up + pulse)
   - Révélations cascadées avec zoom/fondu
   - Sweep lumineux qui traverse le slide
   - Vignette dynamique
   - Icône pulsante
   ------------------------------------------------------------- */
function ThemeSlide({ theme, index }) {
  const style = ACCENT_STYLES[theme.accent] || ACCENT_STYLES.red;
  const Icon = style.icon;
  const chapterNum = String(index + 1).padStart(2, '0');
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div
      key={theme.num}
      className={`w-full h-full flex flex-col bg-gradient-to-br ${style.grad} text-white relative overflow-hidden`}
      data-testid={`ceremonie-theme-${theme.num}`}
      style={{ animation: 'slide-master-fade 800ms cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      {/* ═════ COUCHE 1 : Halos Ken Burns (dérive + zoom en boucle) ═════ */}
      <div
        className="absolute w-[720px] h-[720px] rounded-full bg-white/15 blur-3xl pointer-events-none"
        style={{ top: '-15%', right: '-15%', animation: 'ken-burns-1 18s ease-in-out infinite alternate' }}
      />
      <div
        className="absolute w-[720px] h-[720px] rounded-full bg-black/45 blur-3xl pointer-events-none"
        style={{ bottom: '-15%', left: '-15%', animation: 'ken-burns-2 22s ease-in-out infinite alternate' }}
      />
      <div
        className="absolute w-[400px] h-[400px] rounded-full pointer-events-none opacity-25"
        style={{
          top: '30%',
          left: '40%',
          background: `radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)`,
          animation: 'ken-burns-3 14s ease-in-out infinite alternate',
        }}
      />

      {/* ═════ COUCHE 2 : Grain textural + vignette ═════ */}
      <div
        className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.08) 0.5px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(0,0,0,0.15) 0.5px, transparent 1px)',
          backgroundSize: '3px 3px, 5px 5px',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.35) 100%)',
          animation: 'vignette-in 1200ms ease-out',
        }}
      />

      {/* ═════ COUCHE 3 : Particules lumineuses flottantes ═════ */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(14)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${(i * 7.3) % 100}%`,
              bottom: `-10px`,
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              opacity: 0.15 + ((i % 4) * 0.08),
              filter: 'blur(0.5px)',
              animation: `float-up ${9 + (i % 5) * 2}s ${(i * 0.6) % 6}s linear infinite`,
              boxShadow: '0 0 6px rgba(255,255,255,0.6)',
            }}
          />
        ))}
      </div>

      {/* ═════ COUCHE 4 : Sweep lumineux qui traverse au montage ═════ */}
      <div
        className="absolute inset-y-0 w-[40%] pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
          animation: 'sweep-once 1500ms cubic-bezier(0.4, 0, 0.2, 1) both',
        }}
      />

      {/* Chiffre chapitre en filigrane XXL avec pulse discret */}
      <div
        className="absolute -bottom-16 -right-6 text-white/[0.06] font-black leading-none pointer-events-none select-none"
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 'clamp(280px, 40vw, 520px)',
          animation: 'chapter-breathe 6s ease-in-out infinite',
        }}
      >
        {chapterNum}
      </div>

      {/* ═════ Bandeau top — code + kicker (fondu depuis la gauche) ═════ */}
      <div
        className="relative z-10 flex items-center justify-between px-6 sm:px-12 md:px-16 pt-6 sm:pt-8"
        style={{ animation: 'fade-slide-right 600ms 100ms both cubic-bezier(0.22,1,0.36,1)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur border-2 border-white/25 flex flex-col items-center justify-center" style={{ animation: 'badge-pop 500ms 300ms both cubic-bezier(0.34,1.56,0.64,1)' }}>
            <span className="text-[8px] font-black opacity-70 leading-none">Séq.</span>
            <span className="text-sm font-black leading-none">{theme.num}</span>
          </div>
          <div className="border-l-2 border-white/20 pl-3">
            <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.28em] opacity-75">
              Chapitre {chapterNum} · Cérémonie AM
            </div>
            <div className="text-xs sm:text-sm font-black uppercase tracking-widest opacity-95">
              {theme.kicker}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {theme.videoId && (
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur border-2 border-white/30 hover:border-white/60 active:scale-95 text-white font-black uppercase tracking-widest text-[10px] sm:text-xs transition-all shadow-lg`}
              style={{ animation: 'video-btn-pulse 2.5s ease-in-out infinite' }}
              data-testid={`ceremonie-video-btn-${theme.num}`}
              aria-label="Ouvrir la vidéo pédagogique"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span className="hidden sm:inline">Vidéo</span>
            </button>
          )}
          <Icon
            className="w-8 h-8 sm:w-10 sm:h-10 opacity-40"
            style={{ animation: 'icon-pulse 3s ease-in-out infinite' }}
          />
        </div>
      </div>

      {/* ═════ Contenu principal — SCROLLABLE avec typo unifiée ═════ */}
      <div className="relative z-10 flex-1 flex flex-col overflow-y-auto px-4 sm:px-8 md:px-12 py-4 sm:py-6">
        <div className="max-w-6xl mx-auto w-full">
        {/* Titre héroïque — révélation cinéma */}
        <h2
          className="text-[26px] sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.08] mb-4 sm:mb-5 max-w-5xl tracking-tight"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            animation: 'title-cinema 900ms 200ms both cubic-bezier(0.22,1,0.36,1)',
            transformOrigin: 'left center',
          }}
        >
          {theme.title}
        </h2>

        {/* Lead éditorial */}
        {theme.lead && (
          <blockquote
            className="relative pl-5 sm:pl-6 border-l-4 border-white/50 mb-5 sm:mb-6 max-w-4xl"
            style={{ animation: 'quote-slide 800ms 350ms both cubic-bezier(0.22,1,0.36,1)' }}
          >
            <div
              className="absolute -left-3 -top-4 text-white/20 text-5xl leading-none font-black select-none"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                animation: 'quote-mark-drop 1200ms 350ms both cubic-bezier(0.68,-0.55,0.27,1.55)',
              }}
            >
              &ldquo;
            </div>
            <p
              className="text-[15px] sm:text-lg md:text-xl font-black leading-snug italic text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {theme.lead}
            </p>
          </blockquote>
        )}

        {/* Contenu principal selon type */}
        <div style={{ animation: 'panel-rise 700ms 550ms both cubic-bezier(0.22,1,0.36,1)' }}>
          {/* LIST : grille label + description */}
          {theme.type === 'list' && theme.list && (
            <div className="grid sm:grid-cols-2 gap-3">
              {theme.list.map((item, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-black/35 backdrop-blur-md border border-white/15 p-4"
                  style={{ animation: `scene-in 500ms ${700 + i * 120}ms both cubic-bezier(0.22,1,0.36,1)` }}
                >
                  <div className={`text-[10px] font-black uppercase tracking-[0.28em] ${style.text} mb-1.5`}>
                    {item.label}
                  </div>
                  <div className="text-sm sm:text-base font-black leading-snug text-white">
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CARDS3 : 3 cartes glass horizontales */}
          {theme.type === 'cards3' && theme.cards && (
            <div className="grid md:grid-cols-3 gap-3">
              {theme.cards.map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl backdrop-blur-md border-2 p-4 sm:p-5 relative overflow-hidden"
                  style={{
                    borderColor: 'rgba(255,255,255,0.25)',
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.14), rgba(0,0,0,0.28))',
                    animation: `stat-zoom-in 900ms ${700 + i * 200}ms both cubic-bezier(0.34,1.56,0.64,1)`,
                  }}
                >
                  <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/15 blur-2xl pointer-events-none" style={{ animation: 'halo-pulse 3s ease-in-out infinite' }} />
                  <div className={`relative text-[10px] font-black uppercase tracking-[0.28em] ${style.text} mb-2 flex items-center gap-1.5`}>
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${style.solid} text-white text-[10px] tabular-nums`}>{i + 1}</span>
                    {card.title}
                  </div>
                  <div className="relative text-sm sm:text-base font-black leading-snug text-white">
                    {card.desc}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SCENARIOS : grille scénarios "changes → solution" */}
          {theme.type === 'scenarios' && theme.scenarios && (
            <div className="grid md:grid-cols-2 gap-3">
              {theme.scenarios.map((sc, i) => (
                <div
                  key={sc.id || i}
                  className="rounded-2xl bg-black/40 backdrop-blur-md border border-white/15 p-4 flex flex-col"
                  style={{ animation: `scene-in 500ms ${700 + i * 150}ms both cubic-bezier(0.22,1,0.36,1)` }}
                >
                  <div className={`text-[10px] font-black uppercase tracking-[0.28em] ${style.text} mb-1`}>
                    Scénario {i + 1}
                  </div>
                  <div className="text-base sm:text-lg font-black leading-tight text-white mb-1.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {sc.title}
                  </div>
                  {sc.question && (
                    <p className="text-xs sm:text-sm italic text-white/70 mb-2">{sc.question}</p>
                  )}
                  {sc.changes && (
                    <ul className="space-y-1 mb-2.5">
                      {sc.changes.map((c, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs sm:text-sm">
                          <span className="text-white/40 mt-0.5">•</span>
                          <span className="leading-snug flex-1">{c}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {sc.solution && (
                    <div className="mt-auto rounded-lg bg-white/95 text-slate-900 p-2.5 text-xs sm:text-sm font-bold leading-snug">
                      <span className={`text-[9px] font-black uppercase tracking-widest ${style.text.replace('300', '600')} block mb-0.5`}>Solution</span>
                      {sc.solution}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ENGAGEMENT : cartes engagements avec check */}
          {theme.type === 'engagement' && theme.engagements && (
            <ul className="grid sm:grid-cols-2 gap-2.5">
              {theme.engagements.map((e, i) => (
                <li
                  key={i}
                  className="rounded-xl bg-white/95 text-slate-900 p-3 sm:p-3.5 flex items-start gap-3 shadow-lg"
                  style={{ animation: `signature-drop 700ms ${700 + i * 120}ms both cubic-bezier(0.22,1,0.36,1)` }}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${style.solid} text-white flex items-center justify-center shadow`}>
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                  <p className="text-sm sm:text-base font-black leading-snug flex-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {e}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Bandeau source ONISR (si présent) */}
        {theme.source && (
          <div
            className="mt-4 sm:mt-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] opacity-60"
            style={{ animation: 'fade-slide-right 500ms 1400ms both ease-out' }}
          >
            <BookOpen className="w-3 h-3" />
            <span>Source : {theme.source}</span>
          </div>
        )}

        {/* Signature « À RETENIR » — DANS le scroll pour ne jamais être coupé */}
        {theme.retenir && (
          <div
            className="mt-5 sm:mt-6 rounded-2xl bg-gradient-to-r from-white via-white to-slate-100 text-slate-900 p-3.5 sm:p-4 flex items-center gap-3 relative overflow-hidden"
            style={{
              animation: 'signature-drop 900ms 1500ms both cubic-bezier(0.22,1,0.36,1)',
              boxShadow: '0 20px 50px -15px rgba(0,0,0,0.5), 0 0 30px rgba(255,255,255,0.15)',
            }}
          >
            <div
              className="absolute inset-y-0 w-1/3 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                animation: 'shimmer-sweep 3s 2200ms cubic-bezier(0.4,0,0.2,1) infinite',
              }}
            />
            <div
              className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full ${style.solid} text-white flex items-center justify-center shadow-lg relative z-10`}
              style={{ animation: 'check-bounce 700ms 1900ms both cubic-bezier(0.34,1.56,0.64,1)' }}
            >
              <Sparkles className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0 relative z-10">
              <div className={`text-[10px] font-black uppercase tracking-[0.24em] ${style.text.replace('300', '600')}`}>
                À retenir
              </div>
              <p
                className="text-sm sm:text-base font-black leading-snug"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {theme.retenir}
              </p>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ═════ Keyframes cinéma broadcast ═════ */}
      <style>{`
        @keyframes slide-master-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ken-burns-1 {
          0%   { transform: translate(0,0) scale(1); }
          100% { transform: translate(-40px,30px) scale(1.15); }
        }
        @keyframes ken-burns-2 {
          0%   { transform: translate(0,0) scale(1); }
          100% { transform: translate(50px,-40px) scale(1.2); }
        }
        @keyframes ken-burns-3 {
          0%   { transform: translate(-50%,-50%) scale(1); opacity: 0.25; }
          50%  { transform: translate(-30%,-40%) scale(1.3); opacity: 0.4; }
          100% { transform: translate(-70%,-60%) scale(0.85); opacity: 0.15; }
        }
        @keyframes float-up {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          10%  { opacity: 0.6; }
          90%  { opacity: 0.4; }
          100% { transform: translateY(-110vh) translateX(30px); opacity: 0; }
        }
        @keyframes sweep-once {
          0%   { transform: translateX(-100%); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(300%); opacity: 0; }
        }
        @keyframes vignette-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes chapter-breathe {
          0%,100% { opacity: 0.06; transform: scale(1); }
          50%     { opacity: 0.10; transform: scale(1.03); }
        }
        @keyframes fade-slide-right {
          from { opacity: 0; transform: translateX(-30px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes badge-pop {
          0%   { opacity: 0; transform: scale(0.5) rotate(-15deg); }
          100% { opacity: 1; transform: none; }
        }
        @keyframes icon-pulse {
          0%,100% { transform: scale(1); opacity: 0.7; }
          50%     { transform: scale(1.15); opacity: 1; }
        }
        @keyframes title-cinema {
          0%   { opacity: 0; transform: translateX(-40px) scale(0.9); letter-spacing: 0.1em; filter: blur(6px); }
          60%  { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: none; letter-spacing: normal; filter: none; }
        }
        @keyframes stat-zoom-in {
          0%   { opacity: 0; transform: scale(0.6) rotate(-6deg); }
          70%  { opacity: 1; transform: scale(1.08) rotate(2deg); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes stat-glow {
          from { text-shadow: 0 8px 32px rgba(0,0,0,0.35), 0 0 20px rgba(255,255,255,0.1); }
          to   { text-shadow: 0 8px 32px rgba(0,0,0,0.35), 0 0 60px rgba(255,255,255,0.35); }
        }
        @keyframes halo-pulse {
          0%,100% { opacity: 0.5; transform: scale(1); }
          50%     { opacity: 0.9; transform: scale(1.25); }
        }
        @keyframes quote-slide {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes quote-mark-drop {
          0%   { opacity: 0; transform: translateY(-40px) scale(0.5) rotate(-25deg); }
          70%  { opacity: 0.3; transform: translateY(4px) scale(1.15) rotate(5deg); }
          100% { opacity: 0.2; transform: none; }
        }
        @keyframes panel-rise {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes bar-grow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes scene-in {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes signature-drop {
          0%   { opacity: 0; transform: translateY(60px) scale(0.95); }
          60%  { opacity: 1; transform: translateY(-6px) scale(1.02); }
          100% { opacity: 1; transform: none; }
        }
        @keyframes check-bounce {
          0%   { opacity: 0; transform: scale(0) rotate(-180deg); }
          70%  { opacity: 1; transform: scale(1.2) rotate(10deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes shimmer-sweep {
          0%   { transform: translateX(-100%); }
          60%  { transform: translateX(400%); }
          100% { transform: translateX(400%); }
        }
        @keyframes video-btn-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.4); }
          50%     { box-shadow: 0 0 0 10px rgba(255,255,255,0); }
        }
      `}</style>

      {/* Modale lecteur vidéo YouTube */}
      {theme.videoId && videoOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={() => setVideoOpen(false)}
          role="dialog"
          data-testid={`ceremonie-video-modal-${theme.num}`}
        >
          <div
            className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              className="absolute -top-1 -right-1 z-10 w-10 h-10 rounded-full bg-white text-black hover:bg-red-500 hover:text-white active:scale-95 flex items-center justify-center shadow-2xl transition"
              aria-label="Fermer la vidéo"
              data-testid="ceremonie-video-close"
            >
              <XIcon className="w-5 h-5" strokeWidth={3} />
            </button>
            <iframe
              className="w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${theme.videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={theme.videoLabel || `Vidéo pédagogique · ${theme.title}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {/* Légende vidéo */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-md text-center px-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-[10px] font-black uppercase tracking-[0.22em]">
              <Film className="w-3 h-3" /> {theme.videoLabel || 'Vidéo pédagogique'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------
   QCM KAHOOT LAUNCHER — lance une session live (QR + réponses temps réel)
   À la fin du Kahoot côté prof, un bouton "Continuer cérémonie" redirige
   automatiquement vers la carte permis.
   ------------------------------------------------------------- */
function QcmKahootLauncher({ onSolo }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const launchKahoot = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quiz/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz_id: quizCeremonie.id,
          quiz_title: quizCeremonie.title,
          questions_count: quizCeremonie.questions.length,
          total_questions: quizCeremonie.questions,
        }),
      });
      if (!res.ok) throw new Error('Impossible de créer la session Kahoot');
      const data = await res.json();
      // Flag pour QuizHost afin d'afficher le bouton "Continuer cérémonie"
      sessionStorage.setItem('ceremonieReturn', '1');
      // Rechargement complet pour éviter le bug SPA (comme QuizLauncher)
      window.location.href = `/quiz/host/${data.code}`;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-black p-6 relative overflow-hidden" data-testid="ceremonie-qcm-launcher">
      {/* Halo doré cérémonie */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-yellow-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-red-500/20 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-3xl w-full text-center space-y-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 text-[10px] sm:text-xs font-black uppercase tracking-[0.24em]">
          <Sparkles className="w-3 h-3" /> QCM Final · Kahoot Live
        </div>

        <h1
          className="text-4xl sm:text-6xl md:text-7xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Prêt pour le<br />
          <span className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 bg-clip-text text-transparent">
            grand QCM final ?
          </span>
        </h1>

        <div className="grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
          {[
            { icon: QrCode, title: 'QR au projecteur', desc: 'Les élèves + parents scannent avec leur téléphone' },
            { icon: Users, title: '10 questions', desc: 'Sur les 7 thèmes officiels S5' },
            { icon: Trophy, title: 'Podium & carte', desc: 'Podium live + carte permis AM à la fin' },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="rounded-2xl bg-white/8 backdrop-blur border border-white/15 p-4 text-left">
                <Icon className="w-5 h-5 text-yellow-300 mb-2" />
                <div className="text-sm font-black text-white leading-tight">{k.title}</div>
                <div className="text-xs text-white/60 mt-0.5 leading-snug">{k.desc}</div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Button
            onClick={launchKahoot}
            disabled={loading}
            size="lg"
            className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black font-black uppercase tracking-widest shadow-2xl px-8 py-6 text-base sm:text-lg"
            data-testid="ceremonie-qcm-launch-kahoot"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Création…</>
            ) : (
              <><QrCode className="w-5 h-5 mr-2" /> Lancer le Kahoot live</>
            )}
          </Button>
          <Button
            onClick={onSolo}
            variant="outline"
            size="lg"
            className="text-white border-white/30 hover:bg-white/10"
            data-testid="ceremonie-qcm-solo"
          >
            QCM solo (fallback)
          </Button>
        </div>

        {error && (
          <div className="rounded-xl bg-red-500/20 border border-red-500/40 p-3 text-red-200 text-sm">
            {error}
          </div>
        )}

        <p className="text-xs text-white/50 max-w-md mx-auto pt-2">
          Le mode Kahoot lance une session projetable avec QR code géant. Les participants scannent depuis leur téléphone et répondent en temps réel. À la fin, tu reviens ici pour générer la carte permis.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   QCM SOLO (fallback) — mode inline si projection impossible
   ------------------------------------------------------------- */
function QcmSlide({ onDone }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = quizCeremonie.questions[current];
  const ans = answers[current];
  const total = quizCeremonie.questions.length;
  const correctCount = useMemo(
    () => Object.values(answers).filter((a) => a.correct).length,
    [answers]
  );
  const done = Object.keys(answers).length === total;
  const pct = Math.round((correctCount / total) * 100);
  const style = ACCENT_STYLES.red;

  const pick = (i) => {
    if (ans) return;
    const correct = q.answer === i;
    setAnswers((prev) => ({ ...prev, [current]: { pick: i, correct, explain: q.explain, correctAnswer: q.answer } }));
  };
  const next = () => setCurrent((c) => (c + 1 < total ? c + 1 : c));

  if (done) {
    const rank =
      pct >= 90 ? { label: 'PILOTE MODÈLE', icon: '🥇', color: 'from-yellow-400 to-amber-600' } :
      pct >= 70 ? { label: 'PRÊT À ROULER', icon: '🥈', color: 'from-slate-300 to-slate-500' } :
      pct >= 50 ? { label: 'BON PROGRÈS', icon: '🥉', color: 'from-orange-400 to-orange-600' } :
                  { label: 'ENCORE UN TOUR', icon: '🎯', color: 'from-red-500 to-red-700' };
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-black p-6 relative overflow-hidden" data-testid="ceremonie-qcm-result">
        {/* Confettis simples */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-bounce"
              style={{
                left: `${(i * 13) % 100}%`,
                top: `${(i * 17) % 100}%`,
                background: ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#a855f7'][i % 5],
                animationDelay: `${(i * 0.15) % 2}s`,
                animationDuration: `${1.5 + (i % 3) * 0.3}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-2xl w-full text-center space-y-6">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${rank.color} text-white text-sm font-black uppercase tracking-widest shadow-2xl`}>
            <span className="text-2xl">{rank.icon}</span> {rank.label}
          </div>
          <div
            className="text-8xl sm:text-9xl font-black text-white tabular-nums leading-none"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {correctCount}
            <span className="text-4xl sm:text-5xl text-white/50">/{total}</span>
          </div>
          <p className="text-lg text-white/80">
            {pct >= 70
              ? "Tu as bien assimilé les 7 thèmes officiels. Prêt pour la cérémonie."
              : "Tes réflexes sont en place mais quelques thèmes méritent une relecture. On continue quand même vers ta cérémonie."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              variant="outline"
              onClick={() => { setCurrent(0); setAnswers({}); }}
              className="text-white border-white/40 hover:bg-white/10"
              data-testid="ceremonie-qcm-restart"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Rejouer
            </Button>
            <Button
              onClick={onDone}
              size="lg"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black uppercase tracking-widest shadow-2xl"
              data-testid="ceremonie-qcm-continue"
            >
              <Trophy className="w-5 h-5 mr-2" /> Voir ma cérémonie
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full flex flex-col bg-gradient-to-br ${style.grad} text-white p-4 sm:p-8 md:p-12 relative overflow-hidden`} data-testid="ceremonie-qcm">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto w-full flex-1 flex flex-col justify-center">
        {/* Progression QCM */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] sm:text-xs font-black uppercase tracking-[0.24em] opacity-80">
            QCM Final · Question {current + 1}/{total}
          </div>
          <div className="text-xs font-black opacity-80">
            {correctCount} ✓
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-black/30 mb-6 overflow-hidden">
          <div
            className="h-full bg-white transition-all duration-500"
            style={{ width: `${((current + (ans ? 1 : 0)) / total) * 100}%` }}
          />
        </div>

        {/* Question */}
        <h2
          className="text-2xl sm:text-3xl md:text-4xl font-black leading-tight mb-6"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {q.q}
        </h2>

        {/* Choix */}
        <div className="grid sm:grid-cols-2 gap-3">
          {q.choices.map((choice, i) => {
            const isRight = ans && q.answer === i;
            const isWrong = ans && ans.pick === i && !ans.correct;
            const disabled = !!ans;
            return (
              <button
                key={i}
                type="button"
                onClick={() => pick(i)}
                disabled={disabled}
                className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 flex items-center gap-4 transition-all ${
                  !ans
                    ? 'border-white/25 bg-black/25 hover:bg-black/40 hover:border-white/50 active:scale-[0.98]'
                    : isRight
                    ? 'border-emerald-400 bg-emerald-500/25 scale-[1.02]'
                    : isWrong
                    ? 'border-orange-400 bg-orange-500/25'
                    : 'border-white/10 bg-black/15 opacity-40'
                }`}
                data-testid={`ceremonie-qcm-choice-${i}`}
              >
                <span
                  className={`flex-shrink-0 w-10 h-10 rounded-xl font-black flex items-center justify-center text-base ${
                    isRight ? 'bg-emerald-400 text-black' : isWrong ? 'bg-orange-400 text-black' : 'bg-white/15 text-white'
                  }`}
                >
                  {['A', 'B', 'C', 'D'][i]}
                </span>
                <span className="flex-1 text-sm sm:text-base font-bold leading-snug">{choice}</span>
                {isRight && <Check className="w-5 h-5 text-emerald-300" strokeWidth={3} />}
                {isWrong && <XIcon className="w-5 h-5 text-orange-300" strokeWidth={3} />}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {ans && (
          <div className="mt-5 rounded-2xl bg-black/40 backdrop-blur border border-white/20 p-4 sm:p-5 animate-in fade-in duration-200">
            <div className={`text-xs font-black uppercase tracking-widest mb-1 ${ans.correct ? 'text-emerald-300' : 'text-orange-300'}`}>
              {ans.correct ? '✓ Bonne réponse' : '✗ Pas cette fois'}
            </div>
            <p className="text-sm sm:text-base leading-relaxed">{ans.explain}</p>
            <Button
              onClick={next}
              size="lg"
              className="w-full mt-4 bg-white text-black hover:bg-white/90 font-black uppercase tracking-widest"
              data-testid="ceremonie-qcm-next"
            >
              {current + 1 < total ? 'Question suivante' : 'Voir mon score'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   Slide CÉRÉMONIE FINALE — utilise CartePermisAM (composant réutilisable)
   ------------------------------------------------------------- */
function CarteAmSlide({ nom, prenom, onRestart }) {
  const [pdfOpen, setPdfOpen] = useState(false);
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-black p-4 sm:p-10 relative overflow-hidden" data-testid="ceremonie-carte">
      <div className="relative z-10 max-w-4xl w-full flex flex-col items-center space-y-6">
        <CartePermisAM nom={nom} prenom={prenom} confettis showTitle showMention />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2 z-20 flex-wrap justify-center">
          <Button
            onClick={() => setPdfOpen(true)}
            size="lg"
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-black uppercase tracking-widest shadow-2xl"
            data-testid="ceremonie-open-pdf"
          >
            <FileDown className="w-4 h-4 mr-2" /> Signer et télécharger le PDF
          </Button>
          <Button
            onClick={onRestart}
            variant="outline"
            className="text-white border-white/40 hover:bg-white/10"
            data-testid="ceremonie-restart"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Recommencer la cérémonie
          </Button>
          <Button
            asChild
            size="lg"
            className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black font-black uppercase tracking-widest shadow-2xl"
            data-testid="ceremonie-back-am"
          >
            <Link to="/permis-am" reloadDocument>
              <Home className="w-4 h-4 mr-2" /> Retour au permis AM
            </Link>
          </Button>
        </div>
      </div>

      {pdfOpen && (
        <SignaturePdfModal
          nom={nom}
          prenom={prenom}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------
   Modal SignaturePdf — coche des engagements + télécharge le PDF
   ------------------------------------------------------------- */
const DEFAULT_ENGAGEMENTS = [
  'Je ne regarde jamais mon téléphone en roulant.',
  'Je mets mon équipement complet, même pour un trajet court.',
  'Je ne roule pas au-dessus de mon niveau.',
  'Je ne conduis pas si je ne suis pas en état.',
  'Je conserve toujours une solution de retour (plan B).',
  'Je ne prends pas un risque uniquement pour impressionner quelqu\'un.',
];

function SignaturePdfModal({ nom, prenom, onClose }) {
  const [checked, setChecked] = useState(() => DEFAULT_ENGAGEMENTS.map(() => true));
  const [signName, setSignName] = useState(() => `${prenom} ${nom}`);
  const [autoEcole, setAutoEcole] = useState('');
  const [naissance, setNaissance] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedCount = checked.filter(Boolean).length;
  const canSubmit = selectedCount >= 1 && signName.trim().length >= 3 && !loading;

  const toggle = (i) => setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const download = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const engagements = DEFAULT_ENGAGEMENTS.filter((_, i) => checked[i]);
      const [firstName, ...lastParts] = signName.trim().split(/\s+/);
      const lastName = lastParts.join(' ') || nom;
      const payload = {
        nom: (lastName || nom).toUpperCase(),
        prenom: firstName || prenom,
        engagements,
        naissance: naissance.trim() || undefined,
        auto_ecole: autoEcole.trim() || undefined,
      };
      const res = await fetch(`${BACKEND_URL}/api/ceremonie-am/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = `${payload.prenom}-${payload.nom}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.download = `ceremonie-permis-am-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('PDF téléchargé — bonne route !');
      onClose();
    } catch (e) {
      toast.error(`Impossible de générer le PDF (${e.message || 'erreur'}).`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/90 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ceremonie-pdf-modal"
    >
      <div
        className="bg-gradient-to-br from-slate-800 to-slate-950 border-2 border-emerald-500/40 rounded-3xl p-5 sm:p-7 max-w-xl w-full shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-black uppercase tracking-widest mb-3">
            <PenLine className="w-3 h-3" /> Signer & télécharger
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Mes engagements & mon souvenir
          </h2>
          <p className="text-xs sm:text-sm text-white/60 mt-1">
            Coche ce sur quoi tu t'engages. Ton nom apparaîtra en clair sur le PDF.
          </p>
        </div>

        {/* Engagements checkboxes */}
        <div className="space-y-2 mb-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2">
            Mes 6 engagements ({selectedCount}/6)
          </div>
          {DEFAULT_ENGAGEMENTS.map((eng, i) => (
            <label
              key={i}
              className={`flex items-start gap-3 p-3 rounded-xl border transition cursor-pointer ${
                checked[i]
                  ? 'bg-emerald-500/10 border-emerald-500/50'
                  : 'bg-white/[0.02] border-white/10 hover:border-white/25'
              }`}
              data-testid={`ceremonie-eng-toggle-${i}`}
            >
              <input
                type="checkbox"
                checked={checked[i]}
                onChange={() => toggle(i)}
                className="mt-0.5 w-5 h-5 accent-emerald-500 flex-shrink-0 cursor-pointer"
              />
              <span className="text-sm text-white/90 font-semibold leading-snug">
                {eng}
              </span>
            </label>
          ))}
        </div>

        {/* Champs optionnels */}
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1 block">
              Auto-école (optionnel)
            </label>
            <input
              type="text"
              value={autoEcole}
              onChange={(e) => setAutoEcole(e.target.value)}
              placeholder="Ex : Auto-École Jallieu"
              maxLength={80}
              className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-emerald-500 outline-none px-3 py-2 text-white text-sm"
              data-testid="ceremonie-input-auto-ecole"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1 block">
              Date de naissance (optionnel)
            </label>
            <input
              type="text"
              value={naissance}
              onChange={(e) => setNaissance(e.target.value)}
              placeholder="jj/mm/aaaa"
              maxLength={12}
              className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-emerald-500 outline-none px-3 py-2 text-white text-sm"
              data-testid="ceremonie-input-naissance"
            />
          </div>
        </div>

        {/* Signature nom en clair */}
        <div className="mb-5">
          <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1 block">
            Ma signature (nom en clair)
          </label>
          <input
            type="text"
            value={signName}
            onChange={(e) => setSignName(e.target.value)}
            placeholder="Prénom Nom"
            maxLength={60}
            className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-emerald-500 outline-none px-4 py-3 text-white font-black tracking-wide"
            data-testid="ceremonie-input-signature"
          />
          <p className="text-[11px] text-white/50 italic mt-1.5">
            En saisissant ton nom, tu confirmes tes engagements. Ce PDF est un souvenir
            pédagogique — l'attestation officielle est délivrée par ton auto-école.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={onClose}
            variant="outline"
            className="text-white border-white/40 hover:bg-white/10"
            disabled={loading}
            data-testid="ceremonie-pdf-cancel"
          >
            <XIcon className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button
            onClick={download}
            disabled={!canSubmit}
            size="lg"
            className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-black uppercase tracking-widest shadow-2xl disabled:opacity-50"
            data-testid="ceremonie-pdf-download"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Télécharger mon PDF</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   Modal saisie nom/prénom pour la carte
   ------------------------------------------------------------- */
function NomModal({ onSubmit }) {
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const canSubmit = nom.trim().length >= 2 && prenom.trim().length >= 2;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-4" data-testid="ceremonie-nom-modal">
      <div className="bg-gradient-to-br from-slate-800 to-slate-950 border-2 border-yellow-500/40 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 text-[10px] font-black uppercase tracking-widest mb-3">
            <Trophy className="w-3 h-3" /> Cérémonie · dernière étape
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Ton nom sur la carte permis
          </h2>
          <p className="text-sm text-white/60 mt-1">Pour la cérémonie finale (visuel uniquement)</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-1 block">Nom</label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="DUPONT"
              maxLength={30}
              className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-yellow-500 outline-none px-4 py-3 text-white font-black uppercase tracking-wide"
              data-testid="ceremonie-input-nom"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-1 block">Prénom</label>
            <input
              type="text"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              placeholder="Jean"
              maxLength={30}
              className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-yellow-500 outline-none px-4 py-3 text-white font-black tracking-wide"
              data-testid="ceremonie-input-prenom"
            />
          </div>
        </div>
        <Button
          onClick={() => canSubmit && onSubmit({ nom: nom.trim(), prenom: prenom.trim() })}
          disabled={!canSubmit}
          size="lg"
          className="w-full mt-6 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black font-black uppercase tracking-widest shadow-2xl"
          data-testid="ceremonie-nom-valider"
        >
          Générer ma carte permis <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   SlideCrossfade — enveloppe qui garde la slide précédente 700ms
   pendant que la nouvelle apparaît (fondu enchaîné cinéma).
   ------------------------------------------------------------- */
function SlideCrossfade({ stepKey, children }) {
  const [current, setCurrent] = useState({ key: stepKey, node: children });
  const [previous, setPrevious] = useState(null);
  const prevKeyRef = useRef(stepKey);

  useEffect(() => {
    if (stepKey !== prevKeyRef.current) {
      // Nouveau step → on garde l'ancien en overlay pendant 700ms
      setPrevious({ key: prevKeyRef.current, node: current.node });
      setCurrent({ key: stepKey, node: children });
      prevKeyRef.current = stepKey;
      const t = setTimeout(() => setPrevious(null), 750);
      return () => clearTimeout(t);
    }
    // Même step, on met à jour le contenu (props internes)
    setCurrent({ key: stepKey, node: children });
    return undefined;
  }, [stepKey, children]);

  return (
    <div className="absolute inset-0">
      {/* Ancienne slide en overlay, fade-out */}
      {previous && (
        <div
          key={`prev-${previous.key}`}
          className="absolute inset-0"
          style={{
            animation: 'crossfade-out 700ms cubic-bezier(0.22,1,0.36,1) forwards',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          {previous.node}
        </div>
      )}
      {/* Nouvelle slide, fade-in */}
      <div
        key={`cur-${current.key}`}
        className="absolute inset-0"
        style={{
          animation: previous ? 'crossfade-in 700ms cubic-bezier(0.22,1,0.36,1)' : undefined,
          zIndex: 4,
        }}
      >
        {current.node}
      </div>
      <style>{`
        @keyframes crossfade-out {
          0%   { opacity: 1; transform: scale(1); filter: blur(0); }
          100% { opacity: 0; transform: scale(1.08); filter: blur(6px); }
        }
        @keyframes crossfade-in {
          0%   { opacity: 0; transform: scale(0.96); filter: blur(4px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
      `}</style>
    </div>
  );
}

/* -------------------------------------------------------------
   Page principale — orchestration
   ------------------------------------------------------------- */
export default function CeremonieAM() {
  // step : 'intro' → 0..6 (themes) → 'qcm-launch' | 'qcm-solo' → 'nom' → 'carte'
  const [step, setStep] = useState('intro');
  const [identite, setIdentite] = useState({ nom: '', prenom: '' });
  const [searchParams] = useSearchParams();
  const rootRef = useRef(null);

  useSeo({
    title: 'Cérémonie de fin de formation AM — Code 2 Roues',
    description:
      "Cérémonie finale du permis AM · 7 thèmes officiels S5 + QCM Kahoot live + carte permis animée · en présence des parents.",
  });

  // Retour depuis QuizHost après le Kahoot → saute direct à l'étape "nom"
  // OU depuis la célébration du livret (36/36) → saute direct à "carte" avec nom pré-rempli.
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const eleveParam = searchParams.get('eleve');
    // Prend le nom depuis l'URL s'il est fourni (deep-link depuis Livret Parcours AM)
    if (eleveParam) {
      const cleaned = eleveParam.trim();
      // Sépare "Léa Dupont" → prenom "Léa" + nom "Dupont"
      const parts = cleaned.split(/\s+/);
      const prenom = parts.shift() || '';
      const nom = (parts.join(' ') || cleaned).toUpperCase();
      setIdentite({ nom, prenom });
    }
    if (stepParam === 'carte') {
      // Si nom déjà fourni via URL → saute direct à la carte
      if (eleveParam) {
        setStep('carte');
      } else {
        setStep('nom');
      }
      sessionStorage.removeItem('ceremonieReturn');
    }
  }, [searchParams]);

  // Navigation
  const goToTheme = (idx) => setStep(idx);
  const nextTheme = useCallback(() => {
    setStep((s) => {
      if (s === 'intro') return 0;
      if (typeof s === 'number') return s + 1 < themes.length ? s + 1 : 'qcm-launch';
      return s;
    });
  }, []);
  const prevTheme = useCallback(() => {
    setStep((s) => {
      if (typeof s === 'number') return s === 0 ? 'intro' : s - 1;
      if (s === 'qcm-launch' || s === 'qcm-solo') return themes.length - 1;
      return s;
    });
  }, []);

  // Clavier flèches — désactivé pendant QCM solo (les touches doivent aller aux boutons de réponse)
  useEffect(() => {
    if (step === 'qcm-solo' || step === 'nom' || step === 'carte') return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); nextTheme(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prevTheme(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nextTheme, prevTheme, step]);

  // Progression globale
  const totalSteps = 1 + themes.length + 1 + 1; // intro + 7 themes + qcm + carte
  const currentIndex =
    step === 'intro' ? 0 :
    typeof step === 'number' ? 1 + step :
    (step === 'qcm-launch' || step === 'qcm-solo') ? 1 + themes.length :
    1 + themes.length + 1;
  const globalPct = Math.round((currentIndex / (totalSteps - 1)) * 100);

  // Bouton plein écran
  const goFullscreen = () => {
    if (rootRef.current?.requestFullscreen) {
      rootRef.current.requestFullscreen().catch(() => {});
    } else {
      enterFullscreen(() => {});
    }
  };

  return (
    <div ref={rootRef} className="fixed inset-0 z-30 bg-black flex flex-col" data-testid="ceremonie-page">
      {/* Topbar mince (masquée en mode plein écran natif via CSS backdrop) */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-black/80 backdrop-blur border-b border-white/10 z-10">
        <Button asChild variant="ghost" size="sm" className="text-white hover:bg-white/10" data-testid="ceremonie-back">
          <Link to="/permis-am" reloadDocument>
            <ArrowLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Quitter</span>
          </Link>
        </Button>

        {/* Progression globale */}
        <div className="flex-1 flex items-center gap-2">
          <div className="text-[9px] sm:text-xs font-black uppercase tracking-widest text-white/60">
            {step === 'intro' && 'Intro'}
            {typeof step === 'number' && `Séq ${themes[step].num} · ${step + 1}/${themes.length}`}
            {step === 'qcm-launch' && 'QCM Kahoot'}
            {step === 'qcm-solo' && 'QCM Solo'}
            {step === 'nom' && 'Ta carte'}
            {step === 'carte' && '🏆 Cérémonie'}
          </div>
          <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 to-yellow-500 transition-all duration-500" style={{ width: `${globalPct}%` }} />
          </div>
        </div>

        <Button onClick={goFullscreen} size="sm" variant="ghost" className="text-white hover:bg-white/10" data-testid="ceremonie-fullscreen">
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Contenu principal (avec crossfade inter-slides 700ms) */}
      <div className="flex-1 relative overflow-hidden">
        <SlideCrossfade stepKey={String(step)}>
          {step === 'intro' && <IntroSlide onStart={() => setStep(0)} />}
          {typeof step === 'number' && <ThemeSlide theme={themes[step]} index={step} />}
          {step === 'qcm-launch' && <QcmKahootLauncher onSolo={() => setStep('qcm-solo')} />}
          {step === 'qcm-solo' && <QcmSlide onDone={() => setStep('nom')} />}
          {step === 'nom' && <NomModal onSubmit={(v) => { setIdentite(v); setStep('carte'); }} />}
          {step === 'carte' && <CarteAmSlide nom={identite.nom} prenom={identite.prenom} onRestart={() => setStep('intro')} />}
        </SlideCrossfade>
      </div>

      {/* Navigation footer (masquée sur intro, qcm, nom, carte) */}
      {(typeof step === 'number' || step === 'intro') && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 bg-black/80 backdrop-blur border-t border-white/10 z-10">
          <Button
            onClick={prevTheme}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            disabled={step === 'intro'}
            data-testid="ceremonie-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>

          {/* Pastilles séquences (15) — plus compactes */}
          <div className="hidden md:flex items-center gap-1 flex-wrap max-w-2xl justify-center">
            {themes.map((t, i) => {
              const active = step === i;
              const done = typeof step === 'number' && step > i;
              return (
                <button
                  key={t.num}
                  onClick={() => goToTheme(i)}
                  className={`w-6 h-6 rounded-full text-[9px] font-black flex items-center justify-center transition-all tabular-nums ${
                    active
                      ? 'bg-white text-black scale-125 shadow-lg'
                      : done
                      ? 'bg-emerald-500/70 text-white'
                      : 'bg-white/15 text-white/70 hover:bg-white/25'
                  }`}
                  data-testid={`ceremonie-dot-${t.num}`}
                  title={`Séquence ${t.num} · ${t.title}`}
                >
                  {t.num}
                </button>
              );
            })}
          </div>

          <Button
            onClick={nextTheme}
            size="sm"
            className="bg-red-600 hover:bg-red-500 text-white font-black"
            data-testid="ceremonie-next"
          >
            {step === 'intro' ? 'Commencer' : (typeof step === 'number' && step === themes.length - 1) ? 'QCM final' : 'Suivant'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
